import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, FileText, Package, Download, MapPin, Mail, Building2,
  MessageSquare, Send, XCircle, CheckCircle, Minus, Trash2, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';
import DashboardWrapper from '../../components/DashboardWrapper';
import api from '../../lib/api';
import { getStatusConfig, fmtPrice, fmtDate } from '../../lib/orderStatus';
import { useAuth } from '../../context/AuthContext';

const PartnerOrderDetail = () => {
  const { id } = useParams();
  const { activeOrg, hasPermission } = useAuth();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Editing state for pending orders
  const [editItems, setEditItems] = useState([]);
  const [globalComment, setGlobalComment] = useState('');

  const currentOrgId = activeOrg?.org_id;
  const orgIdRef = useRef(currentOrgId);
  useEffect(() => { orgIdRef.current = currentOrgId; }, [currentOrgId]);

  // ── Multi-org : rechargement quand l'org ou l'id change ──
  useEffect(() => {
    if (!currentOrgId) {
      setPo(null);
      setEditItems([]);
      setGlobalComment('');
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    loadOrder(id, currentOrgId, controller.signal);
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, currentOrgId]);

  const loadOrder = async (orderId, orgId, signal) => {
    setLoading(true);
    try {
      const params = orgId ? { org_id: orgId } : {};
      const res = await api.get(`/partner/purchase-orders/${orderId}`, { params, signal });
      if (signal?.aborted || orgIdRef.current !== orgId) return;
      setPo(res.data);
      initEditState(res.data);
    } catch (err) {
      if (err.name === 'CanceledError' || err.name === 'AbortError' || signal?.aborted) return;
      if (orgIdRef.current !== orgId) return;
      toast.error('Commande introuvable');
    } finally {
      if (!signal?.aborted && orgIdRef.current === orgId) setLoading(false);
    }
  };

  const initEditState = (order) => {
    if (!order || order.status !== 'pending') return;
    setEditItems((order.snapshot_items || []).map(it => ({
      product_id: it.product_id,
      product_name: it.product_name,
      product_image: it.product_image,
      product_ean: it.product_ean,
      original_quantity: it.quantity,
      confirmed_quantity: it.quantity,
      purchase_price: it.purchase_price,
      free_units: it.free_units || 0,
      total_price_paid: it.total_price_paid,
      partner_comment: '',
      removed: false,
    })));
  };

  const isPending = po?.status === 'pending';
  const isTerminal = ['confirmed', 'confirmed_modified', 'cancelled'].includes(po?.status);
  const canManageOrders = hasPermission('distribution.manage');

  // Compute if modifications exist
  const hasModifications = useMemo(() => {
    return editItems.some(it => it.removed || it.confirmed_quantity !== it.original_quantity);
  }, [editItems]);

  // Confirmed subtotal for preview
  const confirmedSubtotal = useMemo(() => {
    return editItems.reduce((sum, it) => {
      if (it.removed) return sum;
      return sum + it.confirmed_quantity * it.purchase_price;
    }, 0);
  }, [editItems]);

  const updateItemQty = useCallback((idx, value) => {
    setEditItems(prev => prev.map((it, i) => {
      if (i !== idx) return it;
      const qty = Math.max(0, Math.min(it.original_quantity, parseInt(value) || 0));
      return { ...it, confirmed_quantity: qty, removed: qty === 0 };
    }));
  }, []);

  const removeItem = useCallback((idx) => {
    setEditItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, confirmed_quantity: 0, removed: true } : it
    ));
  }, []);

  const restoreItem = useCallback((idx) => {
    setEditItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, confirmed_quantity: it.original_quantity, removed: false } : it
    ));
  }, []);

  const setItemComment = useCallback((idx, comment) => {
    setEditItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, partner_comment: comment } : it
    ));
  }, []);

  const handleConfirm = async () => {
    const allRemoved = editItems.every(it => it.removed);
    if (allRemoved) {
      toast.error('Impossible de confirmer : toutes les lignes sont supprimées. Annulez la commande à la place.');
      return;
    }

    const submitOrgId = orgIdRef.current;
    setUpdating(true);
    try {
      const payload = {
        items: editItems.map(it => ({
          product_id: it.product_id,
          confirmed_quantity: it.removed ? 0 : it.confirmed_quantity,
          partner_comment: it.partner_comment,
        })),
        global_comment: globalComment,
      };
      const confirmParams = submitOrgId ? { org_id: submitOrgId } : {};
      await api.post(`/partner/purchase-orders/${id}/confirm`, payload, { params: confirmParams });
      toast.success(hasModifications ? 'Commande confirmée avec modifications' : 'Commande confirmée');
      if (orgIdRef.current === submitOrgId) {
        await loadOrder(id, submitOrgId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la confirmation');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir annuler cette commande ? Cette action est définitive.')) return;
    const submitOrgId = orgIdRef.current;
    setUpdating(true);
    try {
      const cancelParams = submitOrgId ? { org_id: submitOrgId } : {};
      await api.patch(`/partner/purchase-orders/${id}/status`, { status: 'cancelled' }, { params: cancelParams });
      toast.success('Commande annulée');
      if (orgIdRef.current === submitOrgId) {
        await loadOrder(id, submitOrgId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setUpdating(false);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    const submitOrgId = orgIdRef.current;
    setAddingNote(true);
    try {
      const noteParams = submitOrgId ? { org_id: submitOrgId } : {};
      await api.post(`/partner/purchase-orders/${id}/notes`, { note: noteText.trim() }, { params: noteParams });
      toast.success('Note ajoutée');
      setNoteText('');
      if (orgIdRef.current === submitOrgId) {
        await loadOrder(id, submitOrgId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setAddingNote(false);
    }
  };

  const handleDownload = async () => {
    try {
      const dlOrgId = orgIdRef.current;
      const dlParams = dlOrgId ? { org_id: dlOrgId } : {};
      const res = await api.get(`/partner/purchase-orders/${id}/pdf`, { params: dlParams, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `commande_${po?.order_number || id}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur téléchargement');
    }
  };

  if (loading) {
    return (
      <DashboardWrapper>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </DashboardWrapper>
    );
  }

  if (!po) {
    return (
      <DashboardWrapper>
        <div className="text-center py-16">
          <Package className="h-12 w-12 text-slate-200 mx-auto mb-3" />
          <p className="text-sm text-slate-400">Commande introuvable</p>
          <Link to="/dashboard/partner/commandes">
            <Button variant="outline" className="mt-4 gap-2">
              <ArrowLeft className="h-4 w-4" /> Retour aux commandes
            </Button>
          </Link>
        </div>
      </DashboardWrapper>
    );
  }

  const st = getStatusConfig(po.status);
  const StIcon = st.icon;
  const origTotals = po.snapshot_totals || {};
  const confirmedTotals = po.confirmed_totals || null;
  const displayTotals = confirmedTotals || origTotals;
  const userSnap = po.snapshot_user || {};
  const items = po.snapshot_items || [];
  const notes = po.partner_notes || [];
  const history = po.status_history || [];

  return (
    <DashboardWrapper>
      <div className="space-y-6 max-w-5xl pb-8" data-testid="partner-order-detail">
        {/* Back + Header */}
        <div>
          <Link to="/dashboard/partner/commandes" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="back-to-orders">
            <ArrowLeft className="h-4 w-4" /> Retour aux commandes
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-900" data-testid="order-number">{po.order_number}</h1>
                <p className="text-xs text-slate-500">{fmtDate(po.created_at)}</p>
              </div>
              <Badge className={`ml-2 ${st.color}`} data-testid="order-status">
                <StIcon className="h-3 w-3 mr-1" /> {st.label}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={handleDownload} data-testid="download-btn">
                <Download className="h-4 w-4" /> Télécharger
              </Button>
            </div>
          </div>
        </div>

        {/* Action bar for pending orders */}
        {isPending && canManageOrders && (
          <Card className="border-amber-200 bg-amber-50/50" data-testid="actions-card">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-medium text-amber-800 mb-3">
                Traitez cette commande en confirmant ou modifiant les quantités ci-dessous
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={handleConfirm}
                  disabled={updating}
                  data-testid="action-confirm"
                >
                  <CheckCircle className="h-4 w-4" />
                  {hasModifications ? 'Confirmer avec modifications' : 'Confirmer la commande'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={handleCancel}
                  disabled={updating}
                  data-testid="action-cancel"
                >
                  <XCircle className="h-4 w-4" /> Annuler la commande
                </Button>
              </div>
              {hasModifications && (
                <p className="text-xs text-blue-700 mt-2 font-medium flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Des modifications ont été détectées. Le statut sera "Confirmée avec modifications".
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Terminal state info */}
        {po.status === 'cancelled' && (
          <Card className="border-red-200 bg-red-50/50">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-medium text-red-800">Cette commande a été annulée et ne peut plus être modifiée.</p>
            </CardContent>
          </Card>
        )}
        {(po.status === 'confirmed' || po.status === 'confirmed_modified') && (
          <Card className="border-emerald-200 bg-emerald-50/50">
            <CardContent className="pt-4 pb-4">
              <p className="text-sm font-medium text-emerald-800">
                {po.status === 'confirmed_modified'
                  ? 'Cette commande a été confirmée avec des modifications. Elle ne peut plus être modifiée.'
                  : 'Cette commande a été confirmée. Elle ne peut plus être modifiée.'}
              </p>
              {po.partner_global_comment && (
                <p className="text-xs text-emerald-700 mt-1">Commentaire : {po.partner_global_comment}</p>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Client Info */}
          <Card className="lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Building2 className="h-4 w-4 text-slate-400" /> Client
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="font-medium text-slate-900" data-testid="client-name">{userSnap.nom_magasin || 'Non renseigné'}</p>
              {userSnap.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="h-3.5 w-3.5 text-slate-400" />
                  <span>{userSnap.email}</span>
                </div>
              )}
              {userSnap.adresse && (
                <div className="flex items-start gap-2 text-slate-600">
                  <MapPin className="h-3.5 w-3.5 text-slate-400 mt-0.5" />
                  <div>
                    <p>{userSnap.adresse}</p>
                    <p>{userSnap.code_postal} {userSnap.ville}</p>
                  </div>
                </div>
              )}
              {po.shipping_zone && (
                <div className="pt-2 border-t">
                  <p className="text-xs text-slate-400">Zone de livraison</p>
                  <p className="font-medium text-slate-700">{po.shipping_zone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Products Table */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Package className="h-4 w-4 text-slate-400" /> Produits commandés ({items.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isPending ? (
                <EditableItemsTable
                  items={editItems}
                  onQtyChange={updateItemQty}
                  onRemove={removeItem}
                  onRestore={restoreItem}
                  onCommentChange={setItemComment}
                />
              ) : (
                <ReadOnlyItemsTable items={items} showConfirmed={po.status === 'confirmed_modified'} />
              )}

              {/* Totals */}
              <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                {isPending && hasModifications ? (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400 line-through">Sous-total initial</span>
                      <span className="tabular-nums text-slate-400 line-through">{fmtPrice(origTotals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-700 font-medium">Sous-total confirmé</span>
                      <span className="tabular-nums text-blue-700 font-semibold">{fmtPrice(confirmedSubtotal)}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Sous-total</span>
                    <span className="tabular-nums">{fmtPrice(displayTotals.subtotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">
                    Frais de port
                    {displayTotals.franco_reached && <span className="ml-1 text-emerald-600 text-xs">(Franco atteint)</span>}
                  </span>
                  <span className="tabular-nums">{fmtPrice(displayTotals.shipping_cost)}</span>
                </div>
                <div className="flex justify-between text-base font-bold pt-2 border-t">
                  <span>Total HT</span>
                  <span className="tabular-nums text-indigo-700" data-testid="order-total">
                    {fmtPrice(isPending && hasModifications
                      ? confirmedSubtotal + (displayTotals.shipping_cost || 0)
                      : displayTotals.total
                    )}
                  </span>
                </div>
              </div>

              {/* Global comment for pending */}
              {isPending && (
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <label className="text-xs font-medium text-slate-500 mb-1 block">Commentaire global (optionnel)</label>
                  <input
                    type="text"
                    value={globalComment}
                    onChange={e => setGlobalComment(e.target.value)}
                    placeholder="Ex: Stock limité sur plusieurs références..."
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                    data-testid="global-comment-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Status History */}
        {history.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold">Historique des statuts</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {history.map((evt, idx) => {
                  const evtSt = getStatusConfig(evt.status);
                  return (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                        evt.status === 'cancelled' ? 'bg-red-400' :
                        evt.status === 'confirmed' || evt.status === 'confirmed_modified' ? 'bg-emerald-400' :
                        'bg-blue-400'
                      }`} />
                      <div>
                        <p className="font-medium text-slate-700">
                          {evtSt.label} — {fmtDate(evt.changed_at)}
                        </p>
                        {evt.note && <p className="text-xs text-slate-500 mt-0.5">{evt.note}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Partner Notes */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-slate-400" /> Notes internes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {notes.length > 0 && (
              <div className="space-y-3 mb-4">
                {notes.map((note, idx) => (
                  <div key={note.id || idx} className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <p className="text-sm text-slate-700">{note.text}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{fmtDate(note.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <input
                type="text"
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                placeholder={canManageOrders ? "Ajouter une note interne..." : "Permission requise pour ajouter des notes"}
                className="flex-1 h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                onKeyDown={e => e.key === 'Enter' && canManageOrders && addNote()}
                disabled={!canManageOrders}
                data-testid="note-input"
              />
              <Button size="sm" onClick={addNote} disabled={!canManageOrders || addingNote || !noteText.trim()} className="gap-1.5" data-testid="add-note-btn">
                <Send className="h-3.5 w-3.5" /> Envoyer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardWrapper>
  );
};


/* ── Editable Items Table (for pending orders) ──────────────────── */
const EditableItemsTable = ({ items, onQtyChange, onRemove, onRestore, onCommentChange }) => (
  <div className="space-y-3">
    {items.map((item, idx) => (
      <div
        key={idx}
        className={`p-3 rounded-lg border transition-colors ${
          item.removed
            ? 'bg-red-50/50 border-red-200 opacity-60'
            : item.confirmed_quantity !== item.original_quantity
              ? 'bg-blue-50/50 border-blue-200'
              : 'bg-white border-slate-200'
        }`}
        data-testid={`edit-item-${idx}`}
      >
        <div className="flex items-center gap-3">
          {item.product_image ? (
            <img src={item.product_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
          ) : (
            <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
              <Package className="h-4 w-4 text-slate-300" />
            </div>
          )}

          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{item.product_name}</p>
            {item.product_ean && <p className="text-[10px] text-slate-400 font-mono">{item.product_ean}</p>}
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            {/* Original qty label */}
            <div className="text-center">
              <p className="text-[10px] text-slate-400">Demandé</p>
              <p className="text-sm font-semibold text-slate-700">{item.original_quantity}</p>
            </div>

            {/* Editable confirmed qty */}
            {!item.removed ? (
              <div className="text-center">
                <p className="text-[10px] text-slate-400">Confirmé</p>
                <input
                  type="number"
                  min="0"
                  max={item.original_quantity}
                  value={item.confirmed_quantity}
                  onChange={e => onQtyChange(idx, e.target.value)}
                  className="w-16 h-8 text-center text-sm font-semibold border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300"
                  data-testid={`qty-input-${idx}`}
                />
              </div>
            ) : (
              <div className="text-center">
                <p className="text-[10px] text-red-500">Supprimé</p>
                <p className="text-sm font-semibold text-red-600">0</p>
              </div>
            )}

            {/* Unit price */}
            <div className="text-right hidden sm:block">
              <p className="text-[10px] text-slate-400">Prix unit.</p>
              <p className="text-sm text-slate-600 tabular-nums">{fmtPrice(item.purchase_price)}</p>
            </div>

            {/* Line total */}
            <div className="text-right">
              <p className="text-[10px] text-slate-400">Total</p>
              <p className="text-sm font-bold tabular-nums">
                {item.removed ? fmtPrice(0) : fmtPrice(item.confirmed_quantity * item.purchase_price)}
              </p>
            </div>

            {/* Remove / Restore button */}
            {item.removed ? (
              <Button variant="ghost" size="sm" onClick={() => onRestore(idx)} className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700" data-testid={`restore-btn-${idx}`}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`remove-btn-${idx}`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Comment field */}
        <div className="mt-2">
          <input
            type="text"
            value={item.partner_comment}
            onChange={e => onCommentChange(idx, e.target.value)}
            placeholder="Commentaire sur cette ligne (optionnel)..."
            className="w-full h-7 px-2 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-indigo-300"
            data-testid={`comment-input-${idx}`}
          />
        </div>
      </div>
    ))}
  </div>
);


/* ── Read-Only Items Table (for confirmed/cancelled/legacy orders) ── */
const ReadOnlyItemsTable = ({ items, showConfirmed }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-slate-200">
          <th className="text-left py-2 text-xs font-semibold text-slate-500">Produit</th>
          <th className="text-center py-2 text-xs font-semibold text-slate-500">EAN</th>
          {showConfirmed && <th className="text-center py-2 text-xs font-semibold text-slate-500">Demandé</th>}
          <th className="text-center py-2 text-xs font-semibold text-slate-500">{showConfirmed ? 'Confirmé' : 'Qté'}</th>
          <th className="text-center py-2 text-xs font-semibold text-slate-500">Gratuit</th>
          <th className="text-right py-2 text-xs font-semibold text-slate-500">Prix unit.</th>
          <th className="text-right py-2 text-xs font-semibold text-slate-500">Total</th>
          {showConfirmed && <th className="text-left py-2 text-xs font-semibold text-slate-500 pl-3">Note</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => {
          const isRemoved = item.item_status === 'removed';
          const isModified = item.item_status === 'modified';
          return (
            <tr
              key={idx}
              className={`border-b border-slate-100 last:border-0 ${isRemoved ? 'opacity-50 bg-red-50/30' : isModified ? 'bg-blue-50/30' : ''}`}
              data-testid={`order-item-${idx}`}
            >
              <td className="py-2.5">
                <div className="flex items-center gap-2">
                  {item.product_image && (
                    <img src={item.product_image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                  )}
                  <div>
                    <span className={`font-medium text-slate-900 truncate max-w-[200px] block ${isRemoved ? 'line-through' : ''}`}>
                      {item.product_name}
                    </span>
                    {isRemoved && <span className="text-[10px] text-red-600 font-medium">Supprimé par le distributeur</span>}
                    {isModified && <span className="text-[10px] text-blue-600 font-medium">Quantité modifiée</span>}
                  </div>
                </div>
              </td>
              <td className="text-center text-slate-500 font-mono text-xs">{item.product_ean || '—'}</td>
              {showConfirmed && (
                <td className="text-center text-slate-400">{item.quantity}</td>
              )}
              <td className={`text-center font-semibold ${isRemoved ? 'text-red-600' : isModified ? 'text-blue-700' : ''}`}>
                {showConfirmed ? (item.confirmed_quantity ?? item.quantity) : item.quantity}
              </td>
              <td className="text-center text-emerald-600">{item.free_units || 0}</td>
              <td className="text-right text-slate-600 tabular-nums">{fmtPrice(item.purchase_price)}</td>
              <td className="text-right font-bold tabular-nums">
                {fmtPrice(showConfirmed ? (item.confirmed_total_price_paid ?? item.total_price_paid) : item.total_price_paid)}
              </td>
              {showConfirmed && (
                <td className="text-left text-xs text-slate-500 pl-3 max-w-[150px] truncate">
                  {item.partner_comment || '—'}
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);


export default PartnerOrderDetail;
